import React, { useState, useEffect } from 'react';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import { Accordion, Typography } from '@guildeducationinc/recess';
import { useHits, useRefinementList } from 'react-instantsearch-hooks-web';
import { RefinementListFacetValueSet } from '@src/components/RefinementListFacetValueSet';
import { useDecision } from '@optimizely/react-sdk';
import { getRolesToDisplay, ROLE_MAPPING, RoleVariaions } from '@src/constants/featureFlags';
import { useSearchContext } from '@src/components/Providers/SearchProvider';
import { logDataDogError, addDataDogUserAttribute } from '@src/utilities/datadog';
import { trackFilterEvents } from '../filterHelpers';

export const PathwayRolesFilter: React.FC = () => {
	const { userFromIdToken: user } = useMemberExperienceAuth();
	const [rolesDecision] = useDecision(ROLE_MAPPING);
	const { results } = useHits();
	const { index, facets } = useSearchContext();
	const [selectedChildrenFilterCount, setSelectedChildrenFilterCount] = useState<number>(0);

	const roleVariation = getRolesToDisplay(rolesDecision);
	const attributeToDisplay = (() => {
		switch (roleVariation) {
			case RoleVariaions.GENERIC:
				return 'genericRoles.lvl0';
			case RoleVariaions.DESTINATION:
				return 'destinationRoles.lvl0';
			case RoleVariaions.SPECIFIC:
				return 'specificRoles.lvl0';
			default:
				return null;
		}
	})();

	// Track destination roles variation view
	useEffect(() => {
		if (roleVariation === RoleVariaions.DESTINATION) {
			addDataDogUserAttribute('destination_roles_variation_viewed', true);
		}
	}, [roleVariation]);

	const { items, refine } = useRefinementList({
		attribute: attributeToDisplay || 'specificRoles.lvl0',
		sortBy: ['name'],
	});

	if (!roleVariation || !attributeToDisplay || !facets[attributeToDisplay]) {
		logDataDogError(new Error('No facets found for roles filter'), 'PathwayRolesFilterError', {
			email: user.email,
		});
		return null;
	}

	const FilterTitle: React.FC = () => {
		const selectedFacetsCount = items.filter(facet => facet.isRefined).length;

		return (
			<Typography
				testid='interests-title'
				variant='h6'
			>{`Careers at ${user.employer || 'your employer'} ${
				selectedFacetsCount > 0 ? `(${selectedFacetsCount + selectedChildrenFilterCount})` : ''
			}`}</Typography>
		);
	};

	return (
		<Accordion defaultOpen={true} testid='pathwayRolesFilterAccordion' title={FilterTitle}>
			{items.map(facet => (
				<div key={facet.label}>
					<RefinementListFacetValueSet
						facet={facet}
						filterCategory='roles'
						onChange={facetValue => {
							refine(facetValue);
							const eventName = facet.isRefined ? 'FilterRemoved' : 'FilterAdded';
							trackFilterEvents(
								attributeToDisplay,
								eventName,
								'roles',
								facetValue,
								index,
								results,
								user
							);
						}}
						setSelectedChildrenFilterCount={setSelectedChildrenFilterCount}
					/>
				</div>
			))}
		</Accordion>
	);
};
